import { useQuery } from '@tanstack/react-query';
import { useDomainMutation } from '@pps/mutations';
import { cleanPayload, extractEdgeError } from '@pps/supabase/payload';
import { supabase } from '@/integrations/supabase/client';
import i18n from '@/i18n';

export interface ProfessorTeamMember {
  id: string;
  club_id: string;
  name: string;
  phone: string | null;
  email: string | null;
  specialty: string | null;
  is_active: boolean;
  user_id: string | null;
  /** true quando o professor tem login ativo (club_staff role='teacher' is_active=true). */
  hasAccess: boolean;
}

const TEAM_KEY = ['professor-team'] as const;

/**
 * Pool de professores do espaço "PPS para Professores" (clubs.kind='professor').
 * Lista club_teachers do clube + status de acesso (club_staff role='teacher').
 * Sem FK entre club_teachers e club_staff — join feito client-side por user_id
 * (mesmo padrão de ausência de nested-select quando não há FK direto no schema).
 */
export function useProfessorTeam(clubId: string | undefined, ownerId: string | undefined) {
  const query = useQuery({
    queryKey: [...TEAM_KEY, clubId],
    enabled: !!clubId,
    queryFn: async (): Promise<ProfessorTeamMember[]> => {
      if (!clubId) return [];

      const [{ data: teachers, error: teachersErr }, { data: staff, error: staffErr }] =
        await Promise.all([
          supabase
            .from('club_teachers')
            .select('id, club_id, name, phone, email, specialty, is_active, user_id')
            .eq('club_id', clubId)
            .order('name'),
          supabase
            .from('club_staff')
            .select('user_id, is_active')
            .eq('club_id', clubId)
            .eq('role', 'teacher'),
        ]);

      if (teachersErr) throw teachersErr;
      if (staffErr) throw staffErr;

      const activeStaffUserIds = new Set(
        (staff ?? []).filter(s => s.is_active).map(s => s.user_id)
      );

      // Dono do espaço nunca tem linha em club_staff (acesso vem de
      // clubs.owner_id, não de convite) — sem este OR, a badge de acesso
      // mostrava "Sem acesso" incorretamente pro próprio dono logado.
      return (teachers ?? []).map(t => ({
        ...t,
        hasAccess: (!!t.user_id && t.user_id === ownerId) || (!!t.user_id && activeStaffUserIds.has(t.user_id)),
      }));
    },
  });

  return { ...query, teachers: query.data ?? [] };
}

export interface InviteProfessorInput {
  clubId: string | undefined;
  name: string;
  email: string;
  phone?: string;
  specialty?: string;
  /** Dígitos crus (11). Opcional — gravado fill-if-empty em user_private_data. */
  cpf?: string;
  /** ISO yyyy-mm-dd. Opcional — gravado fill-if-empty em profiles.birth_date. */
  birthDate?: string;
}

/**
 * Convida um professor para o pool via edge function `invite-professor`
 * (owner-only, kind='professor' — ver supabase/functions/invite-professor).
 * Mesmo padrão de tratamento de erro de `useInviteMember` (useClubMembers.ts).
 */
export function useInviteProfessor() {
  return useDomainMutation<unknown, Error, InviteProfessorInput>({
    mutationFn: async ({ clubId, name, email, phone, specialty, cpf, birthDate }) => {
      const { data, error } = await supabase.functions.invoke('invite-professor', {
        body: cleanPayload({ club_id: clubId, name, email, phone, specialty, cpf, birth_date: birthDate }),
      });
      if (error) {
        const code = await extractEdgeError(error);
        if (code === 'rate_limited') throw new Error(i18n.t('common.rateLimited'));
        if (code === 'forbidden') throw new Error(i18n.t('common.forbidden'));
        if (code === 'external_service') throw new Error(i18n.t('common.serviceError'));
        throw error;
      }
      const dataObj = data as { error?: string } | null | undefined;
      if (dataObj?.error) {
        const code = await extractEdgeError(dataObj);
        if (code === 'rate_limited') throw new Error(i18n.t('common.rateLimited'));
        if (code === 'forbidden') throw new Error(i18n.t('common.forbidden'));
        throw new Error(dataObj.error);
      }
      return data;
    },
    invalidates: [TEAM_KEY],
    toast: {
      success: i18n.t('professorTeam.inviteSuccess'),
      error: i18n.t('professorTeam.inviteError'),
    },
    tags: { feature: 'crm', action: 'invite_professor' },
  });
}
